import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, ClipboardList, Award, Trash2, Play } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const RESULT_LABEL = {
  pending: { label: "Pendente", tone: "bg-stone-100 text-stone-700" },
  approved: { label: "Aprovada", tone: "bg-emerald-100 text-emerald-800" },
  approved_with_remarks: { label: "Aprovada c/ ressalvas", tone: "bg-amber-100 text-amber-800" },
  rejected: { label: "Reprovada", tone: "bg-rose-100 text-rose-800" },
};

const newSection = () => ({
  id: crypto.randomUUID(),
  title: "Nova seção",
  items: [],
});

const newItem = () => ({
  id: crypto.randomUUID(),
  text: "",
  weight: 1,
  type: "yes_no",
});

export default function Audits() {
  const [templates, setTemplates] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [openTpl, setOpenTpl] = useState(false);
  const [openExec, setOpenExec] = useState(null);
  const [tplForm, setTplForm] = useState({
    name: "",
    description: "",
    passing_score: 70,
    sections: [newSection()],
  });
  const [execResponses, setExecResponses] = useState({});
  const [execNotes, setExecNotes] = useState("");

  const load = async () => {
    try {
      const [t, e] = await Promise.all([
        api.get("/audits/templates"),
        api.get("/audits/executions"),
      ]);
      setTemplates(t.data);
      setExecutions(e.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addSection = () =>
    setTplForm({ ...tplForm, sections: [...tplForm.sections, newSection()] });

  const updateSection = (sId, patch) =>
    setTplForm({
      ...tplForm,
      sections: tplForm.sections.map((s) => (s.id === sId ? { ...s, ...patch } : s)),
    });

  const removeSection = (sId) =>
    setTplForm({
      ...tplForm,
      sections: tplForm.sections.filter((s) => s.id !== sId),
    });

  const addItem = (sId) =>
    updateSection(sId, {
      items: [...(tplForm.sections.find((s) => s.id === sId)?.items || []), newItem()],
    });

  const updateItem = (sId, iId, patch) => {
    const sec = tplForm.sections.find((s) => s.id === sId);
    updateSection(sId, {
      items: sec.items.map((i) => (i.id === iId ? { ...i, ...patch } : i)),
    });
  };

  const removeItem = (sId, iId) => {
    const sec = tplForm.sections.find((s) => s.id === sId);
    updateSection(sId, { items: sec.items.filter((i) => i.id !== iId) });
  };

  const saveTemplate = async () => {
    if (!tplForm.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    const totalItems = tplForm.sections.reduce((acc, s) => acc + s.items.length, 0);
    if (totalItems === 0) {
      toast.error("Adicione pelo menos uma pergunta");
      return;
    }
    try {
      await api.post("/audits/templates", {
        name: tplForm.name,
        description: tplForm.description,
        passing_score: Number(tplForm.passing_score),
        sections: tplForm.sections,
      });
      toast.success("Template criado");
      setOpenTpl(false);
      setTplForm({ name: "", description: "", passing_score: 70, sections: [newSection()] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar");
    }
  };

  const removeTemplate = async (id) => {
    if (!confirm("Excluir este template de auditoria?")) return;
    try {
      await api.delete(`/audits/templates/${id}`);
      toast.success("Removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const startExec = async (tpl) => {
    try {
      const { data } = await api.post("/audits/executions", { template_id: tpl.id });
      const full = await api.get(`/audits/templates/${tpl.id}`);
      setOpenExec({ ...data, template: full.data });
      setExecResponses({});
      setExecNotes("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao iniciar");
    }
  };

  const completeExec = async () => {
    if (!openExec) return;
    const responses = [];
    for (const sec of openExec.template.sections || []) {
      for (const it of sec.items || []) {
        const r = execResponses[it.id] || {};
        responses.push({
          question_id: it.id,
          value: r.value || "",
          score: r.score || 0,
          notes: r.notes || "",
        });
      }
    }
    try {
      const { data } = await api.post(
        `/audits/executions/${openExec.id}/complete`,
        { responses, notes: execNotes }
      );
      toast.success(`Concluído. Score: ${data.score}% (${data.result})`);
      setOpenExec(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao concluir");
    }
  };

  const stats = useMemo(() => {
    const completed = executions.filter((e) => e.result !== "pending");
    const avg = completed.length
      ? completed.reduce((acc, e) => acc + (e.score || 0), 0) / completed.length
      : 0;
    return {
      tpl: templates.length,
      exec: executions.length,
      avg: avg.toFixed(1),
      approved: executions.filter((e) => e.result === "approved").length,
    };
  }, [templates, executions]);

  return (
    <Layout
      title="Auditorias"
      subtitle="Templates auditáveis, execuções e scoring ponderado"
      actions={
        <Dialog open={openTpl} onOpenChange={setOpenTpl}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="audit-new-template">
              <Plus className="h-4 w-4 mr-2" /> Novo template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">Novo template de auditoria</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label>Nome *</Label>
                  <Input
                    value={tplForm.name}
                    onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
                    placeholder="Ex: Auditoria ANVISA"
                    className="mt-1 bg-white"
                    data-testid="audit-tpl-name"
                  />
                </div>
                <div>
                  <Label>Score aprovação (%)</Label>
                  <Input
                    type="number"
                    value={tplForm.passing_score}
                    onChange={(e) => setTplForm({ ...tplForm, passing_score: e.target.value })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  rows={2}
                  value={tplForm.description}
                  onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })}
                  className="mt-1 bg-white"
                />
              </div>

              {tplForm.sections.map((sec, sIdx) => (
                <div key={sec.id} className="border border-stone-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Input
                      value={sec.title}
                      onChange={(e) => updateSection(sec.id, { title: e.target.value })}
                      placeholder={`Seção ${sIdx + 1}`}
                      className="bg-white font-display font-semibold"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeSection(sec.id)}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                  {sec.items.map((it) => (
                    <div key={it.id} className="grid grid-cols-12 gap-2 mb-2 items-end">
                      <Input
                        value={it.text}
                        onChange={(e) => updateItem(sec.id, it.id, { text: e.target.value })}
                        placeholder="Pergunta"
                        className="col-span-6 bg-white"
                      />
                      <div className="col-span-3">
                        <Select
                          value={it.type}
                          onValueChange={(v) => updateItem(sec.id, it.id, { type: v })}
                        >
                          <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes_no">Sim/Não</SelectItem>
                            <SelectItem value="score">Score 0-5</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Input
                        type="number"
                        step="0.1"
                        value={it.weight}
                        onChange={(e) =>
                          updateItem(sec.id, it.id, { weight: Number(e.target.value) })
                        }
                        placeholder="Peso"
                        className="col-span-2 bg-white"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="col-span-1"
                        onClick={() => removeItem(sec.id, it.id)}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-dashed mt-1"
                    onClick={() => addItem(sec.id)}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Pergunta
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addSection} className="w-full border-dashed">
                <Plus className="h-3 w-3 mr-1" /> Adicionar seção
              </Button>
            </div>
            <DialogFooter>
              <Button
                onClick={saveTemplate}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="audit-tpl-submit"
              >
                Salvar template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <div data-testid="audits-page" className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Templates", value: stats.tpl, icon: ClipboardList },
            { label: "Execuções", value: stats.exec, icon: Award },
            { label: "Aprovadas", value: stats.approved, icon: Award },
            { label: "Score médio", value: `${stats.avg}%`, icon: Award },
          ].map((k) => (
            <Card key={k.label} className="p-5 rounded-xl border-stone-200 bg-white">
              <div className="h-9 w-9 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <k.icon className="h-4 w-4" />
              </div>
              <div className="mt-3 font-display text-3xl font-extrabold tracking-tight">{k.value}</div>
              <div className="text-xs text-stone-500 font-body uppercase tracking-wider mt-1">{k.label}</div>
            </Card>
          ))}
        </div>

        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200">
            <h3 className="font-display font-bold tracking-tight">Templates</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nome</TableHead>
                <TableHead>Seções</TableHead>
                <TableHead>Itens</TableHead>
                <TableHead>Aprovação</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-stone-500 py-10">
                    Nenhum template criado
                  </TableCell>
                </TableRow>
              )}
              {templates.map((t) => (
                <TableRow key={t.id} data-testid={`audit-tpl-${t.id}`}>
                  <TableCell>
                    <div className="font-display font-semibold">{t.name}</div>
                    <div className="text-xs text-stone-500 line-clamp-1">{t.description}</div>
                  </TableCell>
                  <TableCell className="font-body">{t.sections?.length || 0}</TableCell>
                  <TableCell className="font-body">
                    {(t.sections || []).reduce((acc, s) => acc + (s.items?.length || 0), 0)}
                  </TableCell>
                  <TableCell className="font-body">{t.passing_score}%</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={() => startExec(t)}
                      className="mr-2 bg-emerald-700 hover:bg-emerald-800 text-white"
                      data-testid={`audit-start-${t.id}`}
                    >
                      <Play className="h-3 w-3 mr-1" /> Auditar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600"
                      onClick={() => removeTemplate(t.id)}
                    >
                      Excluir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200">
            <h3 className="font-display font-bold tracking-tight">Execuções</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Template</TableHead>
                <TableHead>Auditor</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Resultado</TableHead>
                <TableHead>Concluída em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {executions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-stone-500 py-10">
                    Nenhuma execução
                  </TableCell>
                </TableRow>
              )}
              {executions.map((e) => (
                <TableRow key={e.id} data-testid={`audit-exec-${e.id}`}>
                  <TableCell className="font-display font-semibold">{e.template_name}</TableCell>
                  <TableCell className="font-body text-sm">{e.auditor_name}</TableCell>
                  <TableCell className="font-body">{e.score ? `${e.score.toFixed(1)}%` : "—"}</TableCell>
                  <TableCell>
                    <Badge className={`${RESULT_LABEL[e.result]?.tone} border-none`}>
                      {RESULT_LABEL[e.result]?.label || e.result}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-body text-xs text-stone-500">
                    {e.completed_at ? formatDateTime(e.completed_at) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Execution runner */}
        <Dialog open={!!openExec} onOpenChange={(o) => !o && setOpenExec(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">
                Executar — {openExec?.template?.name}
              </DialogTitle>
            </DialogHeader>
            {openExec && (
              <div className="space-y-4">
                {(openExec.template?.sections || []).map((sec, sIdx) => (
                  <div key={sec.id} className="border border-stone-200 rounded-md">
                    <div className="px-3 py-2 bg-stone-50 font-display font-semibold border-b border-stone-200">
                      {sIdx + 1}. {sec.title}
                    </div>
                    <div className="p-3 space-y-3">
                      {sec.items.map((it) => (
                        <div key={it.id} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-7 font-body text-sm">{it.text}</div>
                          <div className="col-span-5">
                            {it.type === "yes_no" ? (
                              <Select
                                value={execResponses[it.id]?.value || ""}
                                onValueChange={(v) =>
                                  setExecResponses({
                                    ...execResponses,
                                    [it.id]: { ...execResponses[it.id], value: v },
                                  })
                                }
                              >
                                <SelectTrigger className="bg-white">
                                  <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="yes">Sim</SelectItem>
                                  <SelectItem value="no">Não</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                type="number"
                                min={0}
                                max={5}
                                step="0.5"
                                placeholder="0..5"
                                value={execResponses[it.id]?.score ?? ""}
                                onChange={(e) =>
                                  setExecResponses({
                                    ...execResponses,
                                    [it.id]: {
                                      ...execResponses[it.id],
                                      score: Number(e.target.value),
                                    },
                                  })
                                }
                                className="bg-white"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <Label>Notas</Label>
                  <Textarea
                    rows={2}
                    value={execNotes}
                    onChange={(e) => setExecNotes(e.target.value)}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={completeExec}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="audit-exec-complete"
              >
                Concluir auditoria
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
